import { useMutation, useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ADMIN_USERS_QUERY,
  CREATE_ADMIN_USER_MUTATION,
  RESET_ADMIN_USER_PASSWORD_MUTATION,
  SET_ADMIN_USER_STATUS_MUTATION,
  UPDATE_ADMIN_USER_MUTATION,
  USER_ROLE_OPTIONS_QUERY,
} from './users-admin.gql';
import { UserFormDialog, type CreateUserFormValues, type UpdateUserFormValues } from './UserFormDialog';

import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { USER_STATUS_FROM_GQL, USER_STATUS_TO_GQL } from '@/shared/graphql/enums';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';

interface AdminUserRowRaw {
  id: string;
  email: string;
  full_name: string;
  status: string;
  branch_id: string | null;
  role_names: string[];
  failed_login_attempts: number;
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AdminUserRow extends Omit<AdminUserRowRaw, 'status'> {
  status: 'active' | 'inactive' | 'suspended';
}

interface RoleOption {
  id: string;
  name: string;
  description: string | null;
}

interface AdminUsersResponse {
  adminUsers: {
    items: AdminUserRowRaw[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

interface UserRoleOptionsResponse {
  userRoleOptions: RoleOption[];
}

type StatusFilter = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ALL';

function normalizeRoleName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '_');
}

export function UsersAdminPage(): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  const [status_filter, setStatusFilter] = useState<StatusFilter>('ACTIVE');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);

  const { data, loading, refetch } = useQuery<AdminUsersResponse>(ADMIN_USERS_QUERY, {
    variables: {
      offset,
      limit,
      search: search || null,
      status_filter,
    },
  });
  const { data: roles_data } = useQuery<UserRoleOptionsResponse>(USER_ROLE_OPTIONS_QUERY);

  const [createAdminUser, create_state] = useMutation(CREATE_ADMIN_USER_MUTATION);
  const [updateAdminUser, update_state] = useMutation(UPDATE_ADMIN_USER_MUTATION);
  const [setStatus] = useMutation(SET_ADMIN_USER_STATUS_MUTATION);
  const [resetPassword] = useMutation(RESET_ADMIN_USER_PASSWORD_MUTATION);

  const rows: AdminUserRow[] = useMemo(
    () =>
      (data?.adminUsers.items ?? []).map((row) => ({
        ...row,
        status: USER_STATUS_FROM_GQL[row.status as keyof typeof USER_STATUS_FROM_GQL] ?? 'inactive',
      })),
    [data?.adminUsers.items],
  );
  const role_options = roles_data?.userRoleOptions ?? [];
  const page = data?.adminUsers.page ?? { total: 0, offset, limit, has_more: false };
  const roleLabel = (role_name: string): string => {
    const key = normalizeRoleName(role_name);
    return t(`users_admin.role_${key}`, { defaultValue: role_name });
  };

  const onCreate = async (values: CreateUserFormValues | UpdateUserFormValues): Promise<void> => {
    try {
      const input = values as CreateUserFormValues;
      const result = await createAdminUser({
        variables: {
          input: {
            email: input.email,
            password: input.password,
            full_name: input.full_name,
            status: USER_STATUS_TO_GQL[input.status],
            role_ids: input.role_ids,
          },
        },
      });
      assertMutationSucceeded(result, 'create_admin_user_failed');
      setCreating(false);
      showToast({
        tone: 'success',
        title: t('alerts.create_success_title'),
        body: t('alerts.create_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.create_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onUpdate = async (values: CreateUserFormValues | UpdateUserFormValues): Promise<void> => {
    if (!editing) return;
    try {
      const input = values as UpdateUserFormValues;
      const result = await updateAdminUser({
        variables: {
          id: editing.id,
          input: {
            email: input.email,
            full_name: input.full_name,
            status: USER_STATUS_TO_GQL[input.status],
            role_ids: input.role_ids,
          },
        },
      });
      assertMutationSucceeded(result, 'update_admin_user_failed');
      setEditing(null);
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onSetStatus = async (
    row: AdminUserRow,
    status: 'active' | 'inactive' | 'suspended',
  ): Promise<void> => {
    if (!window.confirm(t('users_admin.confirm_status_change'))) return;
    try {
      const result = await setStatus({
        variables: {
          id: row.id,
          status: USER_STATUS_TO_GQL[status],
        },
      });
      assertMutationSucceeded(result, 'set_admin_user_status_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onResetPassword = async (row: AdminUserRow): Promise<void> => {
    const next_password = window.prompt(t('users_admin.prompt_new_password'));
    if (!next_password || next_password.trim().length < 8) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: t('users_admin.password_min_error'),
      });
      return;
    }
    try {
      const result = await resetPassword({
        variables: {
          input: {
            user_id: row.id,
            new_password: next_password.trim(),
          },
        },
      });
      assertMutationSucceeded(result, 'reset_admin_user_password_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('users_admin.password_reset_success'),
      });
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const columns: CrudColumn<AdminUserRow>[] = [
    {
      key: 'full_name',
      header_key: 'users_admin.full_name',
      render: (r) => (
        <div>
          <div className="font-medium">{r.full_name}</div>
          <div className="font-mono text-xs text-muted-foreground">{r.email}</div>
        </div>
      ),
    },
    {
      key: 'roles',
      header_key: 'users_admin.roles',
      render: (r) =>
        r.role_names.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {r.role_names.map((name) => (
              <Badge key={name} variant="secondary">
                {roleLabel(name)}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header_key: 'common.status',
      width: 'w-36',
      render: (r) => (
        <Badge
          variant={
            r.status === 'active' ? 'success' : r.status === 'inactive' ? 'muted' : 'outline'
          }
        >
          {r.status === 'suspended' ? t('users_admin.status_suspended') : t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'last_login_at',
      header_key: 'users_admin.last_login',
      width: 'w-44',
      render: (r) =>
        r.last_login_at
          ? new Date(r.last_login_at).toLocaleString('es-MX', {
              dateStyle: 'short',
              timeStyle: 'short',
            })
          : '—',
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-[420px]',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(r)}>
            {t('common.edit')}
          </Button>
          {r.status !== 'active' ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onSetStatus(r, 'active');
              }}
            >
              {t('common.activate')}
            </Button>
          ) : null}
          {r.status !== 'inactive' ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onSetStatus(r, 'inactive');
              }}
            >
              {t('common.deactivate')}
            </Button>
          ) : null}
          {r.status !== 'suspended' ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onSetStatus(r, 'suspended');
              }}
            >
              {t('users_admin.suspend')}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void onResetPassword(r);
            }}
          >
            {t('users_admin.reset_password')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-display text-2xl font-bold tracking-tight">{t('users_admin.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('users_admin.subtitle')}</p>
      </header>

      <CrudTable<AdminUserRow>
        rows={rows}
        columns={columns}
        loading={loading}
        page={page}
        onPageChange={setOffset}
        rowKey={(r) => r.id}
        empty_label_key="users_admin.empty"
        toolbar={
          <CrudToolbar
            search={search}
            onSearchChange={(next) => {
              setSearch(next);
              setOffset(0);
            }}
            filters={
              <select
                value={status_filter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as StatusFilter);
                  setOffset(0);
                }}
                className="border-input bg-background focus:ring-ring h-10 rounded-md border px-3 text-sm shadow-sm focus:outline-none focus:ring-2"
              >
                <option value="ACTIVE">{t('status.active')}</option>
                <option value="INACTIVE">{t('status.inactive')}</option>
                <option value="SUSPENDED">{t('users_admin.status_suspended')}</option>
                <option value="ALL">{t('users_admin.status_all')}</option>
              </select>
            }
            primary_action={
              <Button type="button" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                <span>{t('users_admin.create')}</span>
              </Button>
            }
            placeholder_key="users_admin.search_placeholder"
          />
        }
      />

      {creating ? (
        <UserFormDialog
          mode="create"
          title={t('users_admin.create')}
          role_options={role_options}
          submitting={create_state.loading}
          onSubmit={onCreate}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      {editing ? (
        <UserFormDialog
          mode="update"
          title={t('users_admin.edit')}
          role_options={role_options}
          initial_values={{
            email: editing.email,
            full_name: editing.full_name,
            status: editing.status,
            role_ids: role_options
              .filter((role) => editing.role_names.includes(role.name))
              .map((role) => role.id),
          }}
          submitting={update_state.loading}
          onSubmit={onUpdate}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
